import { useMemo } from 'react';
import uniq from 'hs-lodash/uniq';
import formatName from 'I18n/utils/formatName';
import { useSetUserProgressVisibilityMutation } from '../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.hooks';
import { getQueryFieldName } from 'academy-common-lib/utils/DataFetchingClientCache.new';
import { GET_PROGRESS_ITEMS_FOR_MY_LEARNING_PAGE_QUERY } from '../../../__generated__/chirp/com/hubspot/mylearning/bff/rpc/MyLearningBFFChirpService.dfc.new';
import { useLearningContentCardModels } from '../cards/hooks/useLearningContentCardModels';
import { useUsersForAccount } from '../accounts/accountsQueries';
import getLang from '../../utils/getLang';
import { useQuery } from '../../utils/DataFetchingClient';
export const MY_LEARNING_QUERY_FIELD_NAME = getQueryFieldName(GET_PROGRESS_ITEMS_FOR_MY_LEARNING_PAGE_QUERY);
export const useMyLearning = ({
  variables,
  skip
}) => {
  const {
    data: myLearningData,
    loading: myLearningDataLoading,
    error: myLearningDataError
  } = useQuery(GET_PROGRESS_ITEMS_FOR_MY_LEARNING_PAGE_QUERY, {
    variables,
    skip
  });
  return {
    myLearningData,
    myLearningDataLoading,
    myLearningDataError
  };
};

// retrieves card models for the items retrieved by my learning BFF API
export const useMyLearningCardModels = variables => {
  // first retrieves elements to show
  const {
    myLearningData,
    myLearningDataLoading,
    myLearningDataError
  } = useMyLearning({
    variables
  });
  const assignorIds = useMemo(() => {
    var _myLearningData$resul;
    return uniq(((_myLearningData$resul = myLearningData === null || myLearningData === void 0 ? void 0 : myLearningData.results) !== null && _myLearningData$resul !== void 0 ? _myLearningData$resul : []).map(item => item.assignedBy).filter(id => typeof id === 'number'));
  }, [myLearningData === null || myLearningData === void 0 ? void 0 : myLearningData.results]);
  const {
    usersForAccount,
    usersForAccountLoading
  } = useUsersForAccount({
    userIds: assignorIds,
    skip: assignorIds.length === 0
  });
  const assignorNamesById = useMemo(() => {
    var _usersForAccount$user;
    const map = new Map();
    usersForAccount === null || usersForAccount === void 0 || (_usersForAccount$user = usersForAccount.users) === null || _usersForAccount$user === void 0 || _usersForAccount$user.forEach(user => map.set(user.id, formatName({
      firstName: user.firstName,
      lastName: user.lastName
    })));
    return map;
  }, [usersForAccount === null || usersForAccount === void 0 ? void 0 : usersForAccount.users]);

  // then retrieves hydrated card models for the elements to render
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError,
    userProgressDataLoading
  } = useLearningContentCardModels({
    items: (myLearningData === null || myLearningData === void 0 ? void 0 : myLearningData.results.map(item => {
      const {
        id,
        type,
        assignedBy
      } = item;
      return {
        id: id,
        type: type,
        language: 'language' in item ? item.language : getLang().toUpperCase(),
        // some progress comes back without language -> default to user's language
        assignedByName: typeof assignedBy === 'number' ? assignorNamesById.get(assignedBy) : undefined
      };
    })) || [],
    skip: !myLearningData
  });
  return {
    myLearningCardModels: cardModels,
    myLearningCardModelsLoading: myLearningDataLoading || cardModelsLoading || userProgressDataLoading || usersForAccountLoading,
    myLearningCardModelsError: myLearningDataError || cardModelsError,
    totalCount: (myLearningData === null || myLearningData === void 0 ? void 0 : myLearningData.total) || 0
  };
};
export const useUpdateUserProgressVisibility = variables => {
  return useSetUserProgressVisibilityMutation({
    variables,
    update: (cache, result) => {
      var _result$data;
      if ((_result$data = result.data) !== null && _result$data !== void 0 && _result$data.result) {
        cache.evict({
          fieldName: MY_LEARNING_QUERY_FIELD_NAME
        });
      }
    }
  });
};